import { useState, PropsWithChildren } from "react";
import { ContentModal } from "@/components/ContentModal";
import { Persona } from "@/api/personas";
import { Rating } from "@/components/ui/rating";
import { LoadingButton } from "@/components/LoadingButton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

type CarouselItem = {
  videoSrc?: string;
  imageSrc?: string;
  shareText: string;
  prompt?: string;
  model?: string;
  docId: string;
  rating: number | null;
  approved?: boolean;
  published?: boolean;
};

type ContentReviewModalProps = PropsWithChildren<{
  persona: Persona;
  items: CarouselItem[];
  initialIndex?: number;
  onRate: (itemIndex: number, newValue: number) => void;
  onApprove: (itemIndex: number) => Promise<void>;
  onDelete: (itemIndex: number) => Promise<void>;
}>;

export function ContentReviewModal({
  persona,
  items,
  initialIndex = 0,
  onRate,
  onApprove,
  onDelete,
  children,
}: ContentReviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
  //const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const currentItem = items[currentIndex];

  // Carousel
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
    //setShowPrompt(false);
  };
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    //setShowPrompt(false);
  };

  return (
    <ContentModal
      persona={persona}
      content={
        <div className="flex flex-col h-[70dvh]">
          {/* Main scrollable content area */}
          <div className="flex-1 overflow-y-auto px-2">
            {/* Media */}
            <div className="w-full flex justify-center mb-2">
              {currentItem?.videoSrc ? (
                <video
                  controls
                  className="rounded object-contain max-h-[45vh]"
                  src={currentItem.videoSrc}
                />
              ) : currentItem?.imageSrc ? (
                <img
                  src={currentItem.imageSrc}
                  className="rounded object-contain max-h-[45vh]"
                  alt="Post"
                />
              ) : null}
            </div>

            {/* shareText */}
            <div className="text-sm text-gray-200 mb-2">
              {currentItem?.shareText}
            </div>

            {/* Prompt hidden by default */}
            <div className="w-full mb-2">
              <div className="text-xs text-gray-300 mb-1">
                <span className="font-bold">Model:</span>{" "}
                {currentItem?.model || "No model available"}
              </div>
              <div className="text-xs text-gray-300 mb-1">
                <span className="font-bold">Generated prompt:</span>{" "}
                {currentItem?.prompt || "No prompt available"}
              </div>
            </div>
          </div>

          {/* Fixed bottom section for rating and buttons */}
          <div className="flex flex-col gap-4 mt-4 px-2 py-3 border-t border-gray-800">
            {/* Rating + Approve/Delete */}
            <div className="flex flex-row items-center justify-between gap-5 w-full">
              <div>
                <Rating
                  value={(currentItem?.rating ?? 0) / 2}
                  max={5}
                  precision={0.5}
                  onChange={(val) => onRate(currentIndex, (val ?? 0) * 2)}
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <LoadingButton
                    variant="ghost"
                    className="h-8 text-xs flex-1 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => onDelete(currentIndex)}
                    loading={false}
                  >
                    Delete
                  </LoadingButton>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <LoadingButton
                            variant="default"
                            className="h-8 text-xs"
                            disabled={!!currentItem?.approved}
                            onClick={() => onApprove(currentIndex)}
                            loading={false}
                          >
                            Approve
                          </LoadingButton>
                        </span>
                      </TooltipTrigger>
                      {currentItem?.approved && (
                        <TooltipContent>
                          <p>Already approved</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  {currentItem?.published && (
                    <span className="text-xs text-green-400">(Published)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Carousel if multiple items */}
            {items.length > 1 && (
              <div className="flex flex-row gap-2 justify-center">
                <button
                  className="text-xs text-white underline"
                  onClick={handlePrev}
                >
                  Previous
                </button>
                <button
                  className="text-xs text-white underline"
                  onClick={handleNext}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      }
      /* Footer hidden (no share button)
      footer={
        <div className="flex justify-center p-2">
          <Share url=" " options={{ text: currentItem?.shareText || "", size: "large" }} />
        </div>
      }
      */
    >
      {children}
    </ContentModal>
  );
}
